import {create} from 'zustand';
import {getAgentRebateInfo} from '../agent.service';
import {
  AgentRebateInfoRes,
  AgentRebateGame,
  AgentRebatePerLevel,
  AgentUpgradeLevelInfoRes,
} from '../type';

// 佣金率数据结构
export interface CommissionRateRow {
  rebateForBets: string; // 行头：L1-L4（由组件进行 i18n 渲染）
  teamLevel1: string; // 自身比例 rate
  teamLevel2: string; // level2AgentRate
  teamLevel3: string; // level3AgentRate
  teamLevel4: string; // level4AgentRate
}

export interface CommissionRateData {
  sportsRebates: CommissionRateRow[];
  casinoRebates: CommissionRateRow[];
  lotteryRebates: CommissionRateRow[];
}

// 星级要求数据结构
export interface StarRatingRequirement {
  clubStars: string; // 行头：L1-L4（由组件进行 i18n 渲染）
  agentRequirements: number; // 团队人数要求
  rechargeRequired: string; // 充值要求（格式化字符串）
}

// FAQ 数据结构
export interface FAQItem {
  question: string;
  answer: string;
}

// Store 状态接口
interface RulesStore {
  // 数据状态
  commissionData: CommissionRateData;
  starRatingRequirements: StarRatingRequirement[];
  faqItems: FAQItem[];
  loading: boolean;

  // 方法
  loadData: () => Promise<void>;
}

// Mock 数据
const mockCommissionData: CommissionRateData = {
  sportsRebates: [1, 2, 3, 4].map(l => ({
    rebateForBets: `L${l}`,
    teamLevel1: '0.00%',
    teamLevel2: '0.00%',
    teamLevel3: '0.00%',
    teamLevel4: '0.00%',
  })),
  casinoRebates: [1, 2, 3, 4].map(l => ({
    rebateForBets: `L${l}`,
    teamLevel1: '0.00%',
    teamLevel2: '0.00%',
    teamLevel3: '0.00%',
    teamLevel4: '0.00%',
  })),
  lotteryRebates: [1, 2, 3, 4].map(l => ({
    rebateForBets: `L${l}`,
    teamLevel1: '0.00%',
    teamLevel2: '0.00%',
    teamLevel3: '0.00%',
    teamLevel4: '0.00%',
  })),
};

const mockStarRatingRequirements: StarRatingRequirement[] = [1, 2, 3, 4].map(
  l => ({
    clubStars: `L${l}`,
    agentRequirements: 0,
    rechargeRequired: '0',
  }),
);

const mockFAQItems: FAQItem[] = [
  {
    question: 'Q1: What does commission split mean?',
    answer:
      'A1: After inviting friends to join the application and bind your invitation code, you will receive additional commission based on the consumption of these friends at a certain ratio',
  },
  {
    question: 'Q2: What does club mean?',
    answer: 'A2: Your club consists of all your agent team members',
  },
  {
    question: 'Q3: How can I upgrade to a higher club star rating?',
    answer:
      'A3: You can upgrade by inviting more friends or collectively consuming with team members. For more detailed information, please refer to the upgrade requirements table.',
  },
  {
    question: 'Q4: How do I receive rewards?',
    answer:
      "A4: Once your friends or your friends' invitations make purchases, you will immediately receive all rewards, which will be deposited into your commission account and can be withdrawn to your wallet the next day",
  },
  {
    question:
      'Q5: Are there any wagering requirements for withdrawing rewards?',
    answer: 'A5: There is a one-time wagering requirement.',
  },
  {
    question: 'Q6: When can I use my rewards?',
    answer:
      'A6: You can withdraw your rewards to your wallet for spending the next day. Sometimes there may be slight delays. Thank you for your understanding',
  },
];

// 创建 Store
function toPercent(value?: number): string {
  const v = Number(value ?? 0);
  return `${(v * 100).toFixed(2)}%`;
}

function formatNumber(n?: number): string {
  return Number(n ?? 0).toLocaleString();
}

function buildCommissionRows(
  perLevels: AgentRebatePerLevel[],
): CommissionRateRow[] {
  const byLevel: Record<number, AgentRebatePerLevel> = {};
  perLevels?.forEach(item => {
    if (item?.agentLevel) {
      byLevel[item.agentLevel] = item;
    }
  });
  return [1, 2, 3, 4].map(level => {
    const row = byLevel[level] || ({} as AgentRebatePerLevel);
    return {
      rebateForBets: `L${level}`,
      teamLevel1: toPercent(row?.rate),
      teamLevel2: toPercent(row?.level2AgentRate),
      teamLevel3: toPercent(row?.level3AgentRate),
      teamLevel4: toPercent(row?.level4AgentRate),
    };
  });
}

export const useRulesStore = create<RulesStore>(set => ({
  // 初始状态
  commissionData: mockCommissionData,
  starRatingRequirements: mockStarRatingRequirements,
  faqItems: mockFAQItems,
  loading: false,

  // 加载数据方法
  loadData: async () => {
    set({loading: true});
    try {
      const res: AgentRebateInfoRes = await getAgentRebateInfo();

      const gameMap: Record<string, AgentRebatePerLevel[]> = {};
      (res?.levelInfoResList || []).forEach((g: AgentRebateGame) => {
        gameMap[g.gameName] = g.perLevelInfoResList || [];
      });

      const commissionData: CommissionRateData = {
        sportsRebates: buildCommissionRows(gameMap.Sport || []),
        casinoRebates: buildCommissionRows(gameMap.Casino || []),
        lotteryRebates: buildCommissionRows(gameMap.Lottery || []),
      };

      const levelMap: Record<number, AgentUpgradeLevelInfoRes> = {};
      (res?.upgrateLevelInfoResList || []).forEach(
        (i: AgentUpgradeLevelInfoRes) => {
          levelMap[i.agentLevel] = i;
        },
      );
      const starRatingRequirements: StarRatingRequirement[] = [1, 2, 3, 4].map(
        level => {
          const item = levelMap[level] || ({} as AgentUpgradeLevelInfoRes);
          return {
            clubStars: `L${level}`,
            agentRequirements: item?.teamUserCount ?? 0,
            rechargeRequired: formatNumber(item?.teamRecharge ?? 0),
          };
        },
      );

      set({commissionData, starRatingRequirements, faqItems: mockFAQItems});
    } catch (error) {
      console.error('Failed to load rules data:', error);
      // fallback to mock
      set({
        commissionData: mockCommissionData,
        starRatingRequirements: mockStarRatingRequirements,
      });
    } finally {
      set({loading: false});
    }
  },
}));
